// Nova comanda — um botão só para abrir e para pesar (dono, 26/10/2026: "Pesar
// prato e Nova comanda vão fazer a mesma coisa"). Já vem com o próximo número
// livre, a observação e o cliente (opcionais) e, com balança neste computador
// (Preferências → Balança → Comandas), o peso do prato ao vivo.
//
// - Enter faz a ação principal; Esc (ou fechar) pergunta antes de sair.
// - Sem peso: "Abrir comanda" (ou "Abrir a 05", se o número já está aberto) e
//   entra na comanda para lançar, como sempre.
// - Com peso firme: abre (se precisar), lança o produto da pesagem
//   (Preferências → Balança) com o peso menos o recipiente, fecha a janela e
//   entra na comanda já com o prato dentro (dono, 26/10/2026).
// - Automático: lança sozinho só em comanda JÁ aberta; comanda nova sempre
//   espera o Enter (dá tempo de pôr o nome).
import { Hash, Scale } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { useBalanca, usePrefsBalanca } from "../balanca";
import { MENSAGEM_ESTADO, pesoLiquido } from "../balanca/protocolo";
import { escutar, MENSAGEM_CONEXAO } from "../balanca/servico";
import { comandasApi, erroDe } from "./api";
import CampoCliente, { CAMPO } from "./CampoCliente";
import { itemDoCarrinhoParaPedido, proximoNumero, reais } from "./lib";

const MODULO = "Comandas";
const kgTexto = (kg) => `${Number(kg || 0).toFixed(3).replace(".", ",")} kg`;
const nn = (n) => String(n || 0).padStart(2, "0");
const VAZIO = { nome: "", clienteId: null, telefone: null };

export default function NovaComandaDialog({ open, onClose, abertas, produtos, temBalanca, onEntrar, onLancado }) {
  const prefs = usePrefsBalanca();
  const produto = useMemo(
    () => (temBalanca ? (produtos || []).find((p) => p.id === prefs?.produto_pesagem_id) || null : null),
    [temBalanca, produtos, prefs?.produto_pesagem_id],
  );
  const { ativa, modo, minimoKg, taraGramas } = useBalanca({ modulo: MODULO, produto });
  const pesa = temBalanca;
  const [numero, setNumero] = useState("");
  const [observacao, setObservacao] = useState("");
  const [cliente, setCliente] = useState(VAZIO);
  const [leitura, setLeitura] = useState(null);
  const [firme, setFirme] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [confirmarSair, setConfirmarSair] = useState(false);
  const campoNumero = useRef(null);
  const ocupado = useRef(false); // trava síncrona: Enter + clique juntos não abrem duas comandas

  useEffect(() => {
    if (!open) return;
    setNumero(String(proximoNumero(abertas)));
    setObservacao(""); setCliente(VAZIO); setConfirmarSair(false); setLeitura(null); setFirme(null);
    setTimeout(() => { campoNumero.current?.focus(); campoNumero.current?.select(); }, 50);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- a sugestão vale ao abrir

  useEffect(() => {
    if (!open || !pesa || !ativa) return undefined;
    return escutar(({ leitura: l, firmeKg }) => { setLeitura(l); setFirme(firmeKg); });
  }, [open, pesa, ativa]);

  const num = /^\d{1,4}$/.test(numero) && Number(numero) >= 1 ? Number(numero) : null;
  const comanda = num ? (abertas || []).find((c) => Number(c.numero) === num) || null : null;
  // Número "firme" = sem tecla há 600 ms (01/11/2026): no modo automático o
  // lançamento só pode disparar quando o atendente terminou de digitar — ao
  // digitar "12" com a comanda 01 aberta, o prato ia para a 01 no "1".
  const [numeroFirme, setNumeroFirme] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setNumeroFirme(numero), 600);
    return () => clearTimeout(t);
  }, [numero]);
  const digitando = numeroFirme !== numero;
  const liquido = firme != null ? pesoLiquido(firme, taraGramas) : 0;
  const pesoPronto = pesa && ativa && !!produto && firme != null && firme >= minimoKg && liquido > 0;
  const dadosCliente = () => ({
    numero: num, cliente_nome: cliente.nome.trim() || null, cliente_id: cliente.clienteId,
    cliente_telefone: cliente.telefone, observacao: observacao.trim() || null,
  });

  const rotulo = !num ? "Digite o número"
    : pesoPronto ? (comanda ? `Lançar ${kgTexto(liquido)} na ${nn(num)}` : `Abrir a ${nn(num)} e lançar ${kgTexto(liquido)}`)
    : comanda ? `Abrir a ${nn(num)}` : "Abrir comanda";

  const acao = async () => {
    if (!num || ocupado.current || confirmarSair) return;
    ocupado.current = true;
    setEnviando(true);
    try {
      if (!pesoPronto) {
        // Sem prato na balança: abre (ou entra na já aberta) para lançar itens, como sempre
        const detalhe = comanda ? await comandasApi.obter(comanda.id) : await comandasApi.abrir(dadosCliente());
        if (!comanda) toast.success(`Comanda ${nn(detalhe?.numero ?? num)} aberta`);
        onClose();
        onEntrar?.(detalhe);
        return;
      }
      const alvo = comanda || (await comandasApi.abrir(dadosCliente()));
      const preco = Number(produto.sale_price) || 0;
      const item = itemDoCarrinhoParaPedido({ ...produto, quantity: liquido, final_price: preco, sale_price: preco, pesado_balanca: true, cartItemId: Date.now() });
      const detalhe = await comandasApi.lancar(alvo.id, [item], Math.round(preco * liquido * 100) / 100);
      toast.success(`${produto.name} ${kgTexto(liquido)} · ${reais(preco * liquido)} na comanda ${nn(alvo.numero ?? num)}`);
      onLancado?.();
      onClose();
      onEntrar?.(detalhe);
    } catch (e) {
      toast.error(erroDe(e, "Não foi possível abrir ou lançar"));
    } finally {
      ocupado.current = false;
      setEnviando(false);
    }
  };

  // Automático: comanda já aberta + peso firme + número já digitado = lança sozinho
  useEffect(() => {
    if (open && modo === "automatico" && pesoPronto && comanda && !digitando && !enviando) acao();
  }, [open, modo, pesoPronto, comanda, firme, digitando]); // eslint-disable-line react-hooks/exhaustive-deps

  const pedirSair = (e) => {
    e?.preventDefault?.();
    // Mexer no simulador de balança não é sair da janela
    if (e?.target?.closest?.("[data-e2e^=simulador-balanca]")) return;
    setConfirmarSair(true);
  };

  const aviso = !pesa ? ""
    : !prefs ? "Carregando…"
    : !produto ? "Para pesar o prato, escolha o produto da pesagem em Configurações → Sistema → Preferências → Balança."
    : !ativa ? "A balança não está ligada neste computador para as Comandas."
    : leitura && !["ok", "vazio"].includes(leitura.estado) ? (MENSAGEM_CONEXAO[leitura.estado] || MENSAGEM_ESTADO[leitura.estado] || "")
    : leitura?.estado === "ok" && firme == null ? "Aguardando o peso firmar…"
    : firme != null && firme < minimoKg ? "" : firme != null && liquido <= 0 ? "O peso não passa do recipiente cadastrado no produto" : "";

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) setConfirmarSair(true); }}>
        <DialogContent className="sm:max-w-md" data-e2e="nova-comanda"
          onEscapeKeyDown={pedirSair} onInteractOutside={pedirSair}
          onKeyDown={(e) => { if (e.key === "Enter" && !confirmarSair) { e.preventDefault(); acao(); } }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{pesa && <Scale className="w-5 h-5 text-orange-500" />} Nova comanda</DialogTitle>
            <DialogDescription>
              Enter {pesa ? "abre e lança o prato" : "abre a comanda"} · Esc cancela.
              {pesa && produto ? ` ${produto.name} · ${reais(produto.sale_price)}/kg${taraGramas > 0 ? ` · desconta ${taraGramas} g do prato` : ""}.` : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Nº da comanda</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input ref={campoNumero} value={numero} inputMode="numeric" data-e2e="comanda-numero" placeholder="1"
                  onChange={(e) => setNumero(e.target.value.replace(/\D/g, "").slice(0, 4))} onFocus={(e) => e.target.select()}
                  className={`${CAMPO} pl-9 h-12 text-xl font-black tabular-nums`} />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1" data-e2e="nova-comanda-status">
                {comanda
                  ? `A comanda ${nn(comanda.numero)} já está aberta${comanda.cliente_nome ? ` (${comanda.cliente_nome})` : ""} · ${reais(comanda.total)}`
                  : "Já vem com o próximo número livre; digite ou bipe para trocar."}
              </p>
            </div>
            {!comanda && (<>
              <div>
                <label className="block text-xs font-medium mb-1">Observação</label>
                <input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={200} placeholder="Ex.: mesa da varanda"
                  data-e2e="comanda-observacao" className={CAMPO} />
              </div>
              <CampoCliente open={open} valor={cliente} onChange={setCliente} />
            </>)}

            {pesa && (
              <div className="rounded-xl border bg-muted/30 p-3 text-center" data-e2e="nova-comanda-balanca">
                <p className="text-3xl font-black tabular-nums" data-e2e="pesagem-peso">{leitura?.estado === "ok" ? kgTexto(leitura.kg) : kgTexto(0)}</p>
                {pesoPronto && taraGramas > 0 && <p className="text-xs text-muted-foreground">Sem o prato: {kgTexto(liquido)}</p>}
                {pesoPronto && <p className="text-lg font-bold" data-e2e="pesagem-valor">{reais(liquido * (Number(produto.sale_price) || 0))}</p>}
                {!pesoPronto && !aviso && <p className="text-xs text-muted-foreground">Sem prato: Enter abre a comanda para lançar itens.</p>}
                {aviso && <p className="text-sm text-amber-600 dark:text-amber-400 mt-1" data-e2e="pesagem-aviso">{aviso}</p>}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setConfirmarSair(true)} className="h-10 px-4 rounded-lg border text-sm font-semibold hover:bg-muted">Cancelar</button>
              <button type="button" onClick={acao} disabled={!num || enviando} data-e2e="nova-comanda-confirmar"
                className="h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                {enviando ? "Salvando…" : rotulo}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={open && confirmarSair} onOpenChange={(v) => { if (!v) setConfirmarSair(false); }}>
        <AlertDialogContent data-e2e="nova-comanda-sair">
          <AlertDialogHeader>
            <AlertDialogTitle>Sair da nova comanda?</AlertDialogTitle>
            <AlertDialogDescription>O que foi digitado aqui não será salvo. Itens já lançados continuam nas comandas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-e2e="nova-comanda-continuar">Continuar aqui</AlertDialogCancel>
            <AlertDialogAction data-e2e="nova-comanda-sair-confirmar" onClick={() => { setConfirmarSair(false); onClose(); }}>Sair</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
